import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, FormattedMessage} from 'react-intl';

import {Map, List} from 'immutable';

import * as AcceptFactorChangeMode from 'constants/AcceptFactorChange';

import {makeBet as makeBetAction} from 'actions/creators/MakeBet';
import {acceptRateValueChange as acceptRateValueChangeAction} from 'actions/creators/CouponRate';
import {balance as getBalanceAction} from 'actions/balance/balanceAction';
import {requestBetTypes as requestBetTypesAction} from 'actions/creators/HistoryBet';
import {changeCouponSlipAmount as changeCouponSlipAmountAction} from 'actions/creators/CouponRate';
import {toggleFreebet as toggleFreebetAction} from 'actions/creators/Freebet';

import {isRateUnavailable, rateFactorChangeConfirmNeeded} from './rateUtils';

import BetType from './BetType.jsx';
import FavouriteBetAmount from './FavouriteBetAmount.jsx';

import {minAmount as MinAmountValues} from 'constants/StakeMinAmount';
import {maxWinAmount as MaxWinAmountValues} from 'constants/StakeMaxWinAmount';
import {resourceNames} from 'constants/accountResourceNames';
import { STAKES_VALUES } from '../CouponDropDownMenu.jsx';

const messages = defineMessages({
    makeBet: {
        id: 'coupon.make_bet',
        defaultMessage: 'Сделать ставку'
    },
    confirmRateValueChange: {
        id: 'coupon.confirm_rate_value_change',
        defaultMessage: 'Принять'
    },
    totalOdd: {
        id: 'coupon.total_odd_hint',
        defaultMessage: 'Общий коэффициент'
    },
    possibleWinning: {
        id: 'coupon.possible_winning_hint',
        defaultMessage: 'Возможный выигрыш'
    },
    stake: {
        id: 'coupon.stake_hint',
        defaultMessage: 'Сумма ставки'
    },
    minAmountHint: {
        id: 'coupon.min_amount_hint',
        defaultMessage: 'Мин.'
    },
    maxAmountHint: {
        id: 'coupon.max_amount_hint',
        defaultMessage: 'Макс.'
    }
});

class ControlsExt extends React.Component {
    static propTypes = {
        languageId: React.PropTypes.number.isRequired,
        user: React.PropTypes.object,
        betSlips: React.PropTypes.object,
        betSlipAmounts: React.PropTypes.object,
        currentBetTypeIndex: React.PropTypes.number.isRequired,
        betTypes: React.PropTypes.object,
        amount: React.PropTypes.number,
        odd: React.PropTypes.number,
        possibleWinAmount: React.PropTypes.number,
        makeBetAvailable: React.PropTypes.bool,
        bettingMode: React.PropTypes.object,
        nextKey: React.PropTypes.number,
        bettingAvailable: React.PropTypes.bool.isRequired,
        makeBetAction: React.PropTypes.func.isRequired,
        getBalanceAction: React.PropTypes.func.isRequired,
        acceptRateValueChangeAction: React.PropTypes.func.isRequired,
        requestBetTypesAction: React.PropTypes.func.isRequired,
        changeCouponSlipAmountAction: React.PropTypes.func.isRequired,
        toggleFreebetAction: React.PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this._onMakeBetClick = this._onMakeBetClick.bind(this);
        this._onFavouriteBetAmountClick = this._onFavouriteBetAmountClick.bind(this);
        this._onConfirmClick = this._onConfirmClick.bind(this);
    }

    shouldComponentUpdate(nextProps) {
        return nextProps.languageId !== this.props.languageId ||
            nextProps.betSlips !== this.props.betSlips ||
            nextProps.betSlipAmounts !== this.props.betSlipAmounts ||
            nextProps.amount !== this.props.amount ||
            nextProps.odd !== this.props.odd ||
            nextProps.possibleWinAmount !== this.props.possibleWinAmount ||
            nextProps.currentBetTypeIndex !== this.props.currentBetTypeIndex ||
            nextProps.betTypes !== this.props.betTypes ||
            nextProps.bettingMode !== this.props.bettingMode ||
            nextProps.nextKey !== this.props.nextKey;
    }

    _onMakeBetClick(e) {
        e.preventDefault();

        const {betSlips, betSlipAmounts, bettingMode, currentBetTypeIndex, betTypes} = this.props;

        const slipAmounts = betSlipAmounts.filter(slip => (slip.get('amount') <= 0.0));

        if (slipAmounts.size > 0 || currentBetTypeIndex === -1) {
            return;
        }

        const betType = betTypes.get(currentBetTypeIndex);
        const acceptFactorChangeMode = bettingMode.get('acceptFactorChange');

        let makeBetPossible = true;
        betSlips.forEach((bs) => {
            const rate = bs.get('rate');

            if (rateFactorChangeConfirmNeeded(rate, acceptFactorChangeMode) || isRateUnavailable(rate)) {
                makeBetPossible = false;
                return false;
            }
        });

        if (!makeBetPossible) {
            return;
        }

        let allowIncrease = false;
        let allowDecrease = false;

        if (acceptFactorChangeMode === AcceptFactorChangeMode.INCREASE) {
            allowIncrease = true;
        } else if (acceptFactorChangeMode === AcceptFactorChangeMode.DECREASE) {
            allowDecrease = true;
        } else if (acceptFactorChangeMode === AcceptFactorChangeMode.BOTH) {
            allowIncrease = true;
            allowDecrease = true;
        }

        const betTypeId = betType.type;
        const betTypeRank = betType.rank;

        let betId = this.props.nextKey;

        const bets = [];
        const historyBets = [];

        betSlips.valueSeq().forEach(betSlip => {
            const id = (betId++).toString();
            const rate = betSlip.get('rate');
            const factorValue = rate.get('value');
            const factorId = rate.get('id');

            const transformedMap = new List().push(ControlsExt.transformBetSlip(betSlip));
            const slipAmount = betSlipAmounts.getIn([factorId, 'amount'], 0);
            const isFreebet = betSlipAmounts.getIn([factorId, 'isFreeBet'], false);

            bets.push({
                id,
                type: betTypeId,
                rank: betTypeRank,
                amount: slipAmount,
                factorValue,
                factors: transformedMap,
                oneClick: false,
                allowIncrease,
                allowDecrease,
                isFreebet
            });

            const betSlipMap = new Map().set(factorId, betSlip);

            historyBets.push({
                id,
                betType: betTypeId,
                rank: betTypeRank,
                amount: slipAmount,
                odd: factorValue,
                betSlips: betSlipMap
            });
        });

        if(this.props.user.get('isVerified') === false) {
            window.toggleVerifyModal && window.toggleVerifyModal(true)
        } else {
            this.props.makeBetAction(bets, historyBets);
        }
        this.props.getBalanceAction({accountId: this.props.user.get('accountId')});
    }

    static transformBetSlip(betSlip) {
        const rate = betSlip.get('rate');

        return new Map({
            eventId: rate.get('matchId'),
            eventTimeAndZone: betSlip.getIn(['match', 'timeAndZone']),
            outcomeId: rate.get('outcomeId'),
            factorId: rate.get('id'),
            factorValue: rate.get('value'),
            isConditional: false
        });
    }

    _onFavouriteBetAmountClick(amount) {
        const {betSlips} = this.props;
        let slipAmounts = [];

        betSlips.forEach(slip => {
            const rate = slip.get('rate');
            const rateId = rate.get('id');

            slipAmounts.push({rateId, amount, isFreeBet: false});
        });

        this.props.toggleFreebetAction(false);
        this.props.changeCouponSlipAmountAction(slipAmounts);
    }

    _onConfirmClick(changedRates, e) {
        e.preventDefault();

        this.props.acceptRateValueChangeAction(changedRates);
        this.props.requestBetTypesAction();
    }

    render() {
        const {
            bettingMode,
            betSlips,
            amount,
            odd,
            possibleWinAmount,
            makeBetAvailable,
            bettingAvailable,
            countryCode
        } = this.props;

        const values = STAKES_VALUES[countryCode]
        const favouriteBet1Amount = bettingMode.get('favouriteBet1Amount') || values[1];
        const favouriteBet2Amount = bettingMode.get('favouriteBet2Amount') || values[2];
        const favouriteBet3Amount = bettingMode.get('favouriteBet3Amount') || values[3];

        let makeBetEnabled = makeBetAvailable;
        let confirmRateChangeSpan;
        const changedFactorRates = [];

        betSlips.forEach((r) => {
            const rate = r.get('rate');
            const id = rate.get('id');
            const actualValue = rate.get('actualValue');

            if (rateFactorChangeConfirmNeeded(rate)) {
                changedFactorRates.push({id, actualValue});
            }

            if (makeBetEnabled && isRateUnavailable(rate)) {
                makeBetEnabled = false;
            }
        });

        if (changedFactorRates.length > 0) {
            makeBetEnabled = false;
            const boundConfirmClick = this._onConfirmClick.bind(this, changedFactorRates);

            confirmRateChangeSpan = (
                <div className="btn__accept-changes" onClick={boundConfirmClick}>
                    <a href="#"
                       className="message-box--sell-bet__btn--confirm"
                       onClick={boundConfirmClick}>
                        <FormattedMessage {...messages.confirmRateValueChange} />
                    </a>
                </div>);
        }

        let cls = 'coupon--make-bet make-bet btn__orange make-bet__btn';
        if (!makeBetEnabled || !bettingAvailable) {
            cls += ' btn__disabled';
        }

        return (
            <div className="coupon-controls">
                <BetType/>
                <FormattedMessage {...messages.totalOdd}>
                    {(message) =>
                        <div className="rate" title={message}>
                            <span>{odd.toFixed(2)}</span>
                        </div>}
                </FormattedMessage>
                <FormattedMessage {...messages.possibleWinning}>
                    {(message) =>
                        <div className="balance" title={message}>
                            <span>{possibleWinAmount.toFixed(2)}</span>
                        </div>}
                </FormattedMessage>
                <div className="bet-amount cf">
                    <p className="total-rates"><strong style={{color: '#fff'}}>{amount.toFixed(2)}</strong></p>
                </div>
                <div className="favourites-bet-amounts cf">
                    <FavouriteBetAmount number={1}
                                        amount={favouriteBet1Amount}
                                        callback={this._onFavouriteBetAmountClick}/>
                    <FavouriteBetAmount number={2}
                                        amount={favouriteBet2Amount}
                                        callback={this._onFavouriteBetAmountClick}/>
                    <FavouriteBetAmount number={3}
                                        amount={favouriteBet3Amount}
                                        callback={this._onFavouriteBetAmountClick}/>
                </div>

                {confirmRateChangeSpan}

                <div className={cls}>
                    <FormattedMessage {...messages.makeBet}>
                        {(message) =>
                            <a href="#" className="coupon--make-bet__btn" onClick={this._onMakeBetClick}>{message}</a>}
                    </FormattedMessage>
                </div>
            </div>);
    }
}

function calcTotalAmount(betSlipAmounts) {
    let amount = 0;

    betSlipAmounts.forEach((value) => {
        let slipAmount = Number(value.get('amount'));

        if (!isNaN(slipAmount)) {
            amount += slipAmount;
        }
    });

    return amount;
}

function calcTotalOdd(betSlips) {
    let odd = 0;

    betSlips.forEach((slip) => {
        let rate = slip.get('rate');
        let slipOdd = Number(rate.get('value'));

        if (!isNaN(slipOdd)) {
            odd += slipOdd;
        }
    });

    return odd;
}

function calcTotalWinning(betSlips, betSlipAmounts) {
    let winning = 0;

    betSlips.forEach((slip) => {
        let rate = slip.get('rate');
        let rateId = rate.get('id');
        let slipOdd = Number(rate.get('value'));
        let slipamount = betSlipAmounts.has(rateId) ? betSlipAmounts.getIn([rateId, 'amount']) : 0;

        if (!isNaN(slipOdd) && !isNaN(slipamount)) {
            winning += (slipOdd * slipamount);
        }
    });

    return winning;
}

function isAvailableMakeBet(betSlips, betSlipAmounts, minvalue, maxWinValue) {
    const slips = betSlips.toArray();

    for (let offset = 0; offset < slips.length; offset++) {
        const slip = slips[offset];
        const rate = slip.get('rate');
        const rateId = rate.get('id');
        const slipOdd = Number(rate.get('value'));
        const slipAmount = betSlipAmounts.has(rateId) ? betSlipAmounts.getIn([rateId, 'amount']) : 0;

        if (isNaN(slipAmount) || slipAmount < minvalue) {
            return false;
        }

        if (!isNaN(slipOdd) && slipAmount * slipOdd > maxWinValue) {
            return false;
        }
    }

    return true;
}

function select(state) {
    const languageId = state.application.get('languageId');
    const user = state.auth.get('user');
    const bettingAvailable = state.application.get('bettingAvailable');
    const betSlips = state.application.getIn(['coupon', 'betSlips']);
    const betSlipAmounts = state.application.getIn(['coupon', 'betSlipAmounts']);
    const betTypes = state.application.getIn(['coupon', 'betTypes']);
    const currentBetTypeIndex = state.application.getIn(['coupon', 'currentBetTypeIndex']);
    const bettingMode = state.application.getIn(['bettingMode']);
    const betHistory = state.application.getIn(['betHistory']);

    const resourceName = resourceNames[user.get('resourceId')];
    const maxWinAmount = MaxWinAmountValues[resourceName];
    const minAmountValue = MinAmountValues[resourceName];
    const makeBetAvailable = isAvailableMakeBet(betSlips, betSlipAmounts, minAmountValue, maxWinAmount);
    const amount = makeBetAvailable ? calcTotalAmount(betSlipAmounts) : 0;
    const odd = calcTotalOdd(betSlips);
    const possibleWinAmount = makeBetAvailable ? calcTotalWinning(betSlips, betSlipAmounts) : 0;

    const nextKey = betHistory.size > 0 ? (parseInt(betHistory.keySeq().maxBy(x => parseInt(x))) + 1) : 1;

    return {
        countryCode: state.application.get('countryCode'),
        languageId,
        user,
        betSlips,
        betSlipAmounts,
        betTypes,
        currentBetTypeIndex,
        amount,
        odd,
        possibleWinAmount,
        makeBetAvailable,
        bettingMode,
        nextKey,
        bettingAvailable,
        maxWinAmount
    };
}

export default connect(select, {
    makeBetAction,
    getBalanceAction,
    acceptRateValueChangeAction,
    requestBetTypesAction,
    changeCouponSlipAmountAction,
    toggleFreebetAction
})(ControlsExt);



// WEBPACK FOOTER //
// ./js/sport-betting/components/bet-panel-control/coupon/ControlsExt.jsx